import { PGlite, QueryOptions, Results } from "@electric-sql/pglite";

import { Timings } from "@storyboard/web-logger/timings/Timings";
import { summarizeQuery } from "@/db/summarizeQuery.ts";

export class QueryMonitorImpl {
  private originalQuery: typeof PGlite.prototype.query;
  private pg: PGlite;
  private knownLiveQueryDefinitions: Record<string, string> = {};

  /**
   * Monkey-patch PGlite.query to capture timing data
   *
   * @param pg
   */
  public register(pg: PGlite): void {
    this.pg = pg;
    this.originalQuery = this.pg.query.bind(this.pg);

    this.pg.query = async <T>(sql: string, params?: unknown[], options?: QueryOptions): Promise<Results<T>> => {
      const { query, name } = await this.extractNameAndQuery(sql);
      return Timings.withSpan(
        name,
        {
          category: "Postgres",
          message: query,
          properties: { params, query },
        },
        async (): Promise<Results<T>> => await this.originalQuery<T>(sql, params, options),
      );
    };
  }

  private async extractNameAndQuery(sql: string): Promise<{ query: string; name: string }> {
    const liveQuery = sql.match("^EXECUTE live_query_(?<name>.*)_get;$");

    if (!liveQuery) {
      return {
        query: sql,
        name: summarizeQuery(sql),
      };
    }

    const liveQueryId = liveQuery.groups.name;
    const liveQueryDefinition = await this.resolveLiveQuery(liveQueryId);

    if (liveQueryDefinition) {
      return {
        query: liveQueryDefinition,
        name: `Live query: ${summarizeQuery(liveQueryDefinition)}`,
      };
    }

    return {
      query: sql,
      name: `Live query: live_query_unknown`,
    };
  }

  private async resolveLiveQuery(liveQueryId: string): Promise<string | null> {
    if (!this.knownLiveQueryDefinitions[liveQueryId]) {
      const definition = await this._resolveLiveQuery(liveQueryId);
      if (definition) {
        this.knownLiveQueryDefinitions[liveQueryId] = definition;
      }
    }

    return this.knownLiveQueryDefinitions[liveQueryId];
  }

  private async _resolveLiveQuery(liveQueryId: string): Promise<string | null> {
    try {
      const liveQueryDefinition = await Timings.withSpan(
        `Resolve live query ${liveQueryId}`,
        {
          message: `Resolve live query`,
          category: "Postgres",
        },
        async () =>
          (await this.originalQuery(`select pg_get_viewdef('live_query_${liveQueryId}_view', true) as def;`)).rows[0]?.[
            "def"
          ],
      );

      if (!liveQueryDefinition) {
        console.error(`[QueryMonitor] Could not resolve live query definition`, { liveQueryId });
        return null;
      }

      return liveQueryDefinition;
    } catch (err: unknown) {
      console.error("[QueryMonitor] Error resolving live query definition", err, { liveQueryId });

      return null;
    }
  }
}

export const QueryMonitor = new QueryMonitorImpl();
